import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useRef } from 'react';
import {
  QuestionProgressDtoStatusEnum,
  SectionProgressDtoStatusEnum,
} from '../../../openapi/atlantis';
import {
  ApplicationProgressResponse,
  QuestionProgressDto,
} from '../../../openapi/yenta';
import { cn } from '../../../utils/classUtils';
import StepV2 from './StepV2';
import VerticalStepQuestionsV2 from './VerticalStepQuestionsV2';

interface VerticalStepProgressV2Props {
  progress: ApplicationProgressResponse;
  onNavigateToQuestion: (questionId: string) => void;
  isCollapsed: boolean;
  setIsCollapsed(): void;
}

const VerticalStepProgressV2: React.FC<VerticalStepProgressV2Props> = ({
  progress,
  onNavigateToQuestion,
  isCollapsed,
  setIsCollapsed,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const steps = useMemo(() => progress?.sectionProgresses || [], [progress]);

  const collapsedHeight = useMemo(() => {
    if (!containerRef?.current) return '76px';
    const firstChild = containerRef.current.firstElementChild as HTMLElement;
    return firstChild?.getBoundingClientRect()?.height
      ? `${Math.round(firstChild.getBoundingClientRect()?.height)}px`
      : '76px';
  }, []);

  const getStepProgressPercentage = (
    questionProgress: QuestionProgressDto[],
  ) => {
    const totalCompletedQuestions =
      questionProgress?.filter(
        (q) => q.status === QuestionProgressDtoStatusEnum.Completed,
      )?.length ?? 0;

    const totalQuestionsCount = questionProgress?.length ?? 0;

    return (totalCompletedQuestions / totalQuestionsCount || 0) * 100;
  };

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-white transition-all duration-300',
        isCollapsed ? 'px-5 py-4 gap-y-2' : 'p-4 gap-y-4',
      )}
    >
      <div className='flex flex-col flex-1 overflow-y-auto overflow-clip'>
        <div ref={containerRef}>
          {steps.map((step, index) => {
            const isFinal = index === steps.length - 1;

            const isCompleted =
              step.status === SectionProgressDtoStatusEnum.Completed;

            const isInProgress =
              step.status === SectionProgressDtoStatusEnum.InProgress;

            const inProgressPercentage = getStepProgressPercentage(
              step.questionProgresses!,
            );

            return (
              <div
                key={step.name}
                className='flex'
                style={{
                  height: isCollapsed ? collapsedHeight : 'auto',
                  transition: 'height 0.7s ease',
                }}
              >
                <div className='flex flex-col items-center'>
                  <StepV2
                    inProgressPercentage={inProgressPercentage}
                    isInProgress={isInProgress}
                    isCompleted={isCompleted}
                    isFinal={isFinal}
                    isVertical
                  />
                </div>
                <div
                  className={cn(
                    'pl-5 pt-2 pb-8 transition-opacity duration-300',
                    isCollapsed ? 'opacity-0 hidden' : 'w-full opacity-100',
                  )}
                >
                  <p
                    className={cn(
                      'font-inter font-semibold text-sm',
                      isCompleted || isInProgress
                        ? 'text-reskin-primary-blue'
                        : 'text-primary-dark',
                    )}
                  >
                    {step.name}
                  </p>
                  {isInProgress && (
                    <VerticalStepQuestionsV2
                      step={step}
                      onNavigateToQuestion={onNavigateToQuestion}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div
        className='flex w-8 h-8 p-1.5 rounded border border-reskin-slate-200 bg-white justify-center items-center cursor-pointer transition-all duration-300'
        onClick={setIsCollapsed}
      >
        <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />
      </div>
    </div>
  );
};

export default VerticalStepProgressV2;
